import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calculator, Clock, Zap } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { allCountriesForDropdown } from "../data/visaData";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuotePage() {
  const navigate = useNavigate();
  const [nationality, setNationality] = useState("");
  const [destination, setDestination] = useState("");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGetQuote = async () => {
    if (!nationality || !destination) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quote`, { nationality, destination });
      setQuote(data);
    } catch { setQuote(null); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50" >
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <Calculator className="w-7 h-7 text-blue-600" />
          </div>
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Get a Quote</h1>
          <p className="text-sm text-slate-500">Instant visa fee estimate based on your nationality and destination</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-elevated rounded-2xl p-8">
          <div className="space-y-5">
            <div>
              <label className="text-sm text-slate-500 mb-1.5 block">Your Nationality</label>
              <Input data-testid="quote-nationality" value={nationality} onChange={e => setNationality(e.target.value)} placeholder="e.g. Indian, American, British"
                className="h-12 bg-slate-50 border-slate-200 text-slate-900 rounded-xl placeholder:text-slate-400 focus-visible:ring-blue-500/30 focus-visible:border-blue-500" />
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1.5 block">Destination</label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger data-testid="quote-destination" className="w-full h-12 bg-slate-50 border-slate-200 text-slate-900 rounded-xl focus:ring-blue-500/30">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-900">
                  {allCountriesForDropdown.map(c => (
                    <SelectItem key={c.code} value={c.code} className="focus:bg-blue-50 focus:text-blue-500">
                      <span className="flex items-center gap-2"><span>{c.flag}</span>{c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button onClick={handleGetQuote} disabled={loading || !nationality || !destination} data-testid="get-quote-btn"
              className="w-full h-12 text-white font-semibold bg-blue-600 rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50">
              {loading ? "Calculating..." : "Get Quote"}
            </button>
          </div>

          {quote && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 pt-8 border-t border-slate-100" data-testid="quote-result">
              <h3 className="heading-font text-lg font-semibold text-slate-900 mb-6">Your Estimate</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-blue-600" /><span className="text-sm font-medium text-slate-900">Standard Processing</span></div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Visa Fee</span><span className="text-slate-900">${quote.base_fee}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Service Fee</span><span className="text-slate-900">${quote.service_fee}</span></div>
                    <div className="flex justify-between text-sm pt-2 border-t border-slate-100"><span className="text-slate-900 font-semibold">Total</span><span className="text-blue-600 font-bold text-lg">${quote.total_standard}</span></div>
                    <p className="text-xs text-slate-400 mt-2">{quote.processing_time}</p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                  <div className="flex items-center gap-2 mb-3"><Zap className="w-5 h-5 text-blue-600" /><span className="text-sm font-medium text-blue-500">Express Processing</span></div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Visa Fee</span><span className="text-slate-900">${quote.express_fee}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Service Fee</span><span className="text-slate-900">${quote.service_fee}</span></div>
                    <div className="flex justify-between text-sm pt-2 border-t border-blue-100"><span className="text-slate-900 font-semibold">Total</span><span className="text-blue-600 font-bold text-lg">${quote.total_express}</span></div>
                    <p className="text-xs text-blue-600/60 mt-2">{quote.express_processing}</p>
                  </div>
                </div>
              </div>

              <button onClick={() => navigate("/apply")} data-testid="quote-apply-btn"
                className="w-full h-12 text-white font-semibold bg-blue-600 rounded-xl hover:bg-blue-700 transition-all">
                Apply Now
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
